/* ══════════════════════════════════════════════════════════════════════════
   yl-v9.js · Homepage v9, "One Flame". The stage engine.

   paint(t, g) is a pure function of story time t (and ambient time g, which only
   drives flicker and embers). The page is a timeline; the scrollbar is the playhead:
   t = T0 + scroll progress * (DUR - T0). No IntersectionObserver reveals, no scroll
   events driving CSS transitions; one scroll position always yields one frame.
   The only thing that runs on its own clock is the first-visit opening (t from 0 to
   T0), and it is skipped on repeat visits, on any input, and under reduced motion.

   Silent: this file never touches audio. Loops are muted, fetched only as each age
   approaches, and never loaded under reduced motion or Save-Data.
   Debug: append ?y9debug to expose window.y9.seek(t) for frame-by-frame review.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  const root = document.querySelector('[data-y9]');
  if (!root) return;

  /* ------------------------------------------------------------ motion helpers */
  const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
  const mix = (a, b, p) => a + (b - a) * p;
  // Closed-form damped spring, 0 -> 1 (k stiffness, d damping, unit mass).
  function spring(t, k = 170, d = 26) {
    if (t <= 0) return 0;
    const w0 = Math.sqrt(k), z = d / (2 * w0);
    if (z < 1) { const wd = w0 * Math.sqrt(1 - z * z); return 1 - Math.exp(-z * w0 * t) * (Math.cos(wd * t) + (z * w0 / wd) * Math.sin(wd * t)); }
    return 1 - Math.exp(-w0 * t) * (1 + w0 * t);
  }
  const PRESET = { snappy: [320, 30], base: [170, 26], heavy: [90, 19], drift: [1.2, 2.2] };
  const springNamed = (t, name) => spring(t, PRESET[name][0], PRESET[name][1]);
  function track(t, keys, k, d) { let v = keys[0][1]; for (let i = 1; i < keys.length; i++) v += (keys[i][1] - keys[i - 1][1]) * spring(t - keys[i][0], k, d); return v; }
  const smooth = (t, a, b) => { const x = clamp((t - a) / (b - a)); return x * x * (3 - 2 * x); };
  function rng(seed) { return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const mixColor = (a, b, p) => { const A = hex(a), B = hex(b); return 'rgb(' + A.map((v, i) => Math.round(mix(v, B[i], p))).join(',') + ')'; };

  const Q = new URLSearchParams(location.search);
  const $ = (s, r) => (r || root).querySelector(s);
  const $$ = (s, r) => Array.from((r || root).querySelectorAll(s));
  const RM = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const SAVE = !!(navigator.connection && navigator.connection.saveData);
  const VIDS = !RM && !SAVE;          // loops, embers: off under reduced motion or Save-Data
  const sp = RM ? (t) => (t >= 0.05 ? 1 : 0) : springNamed;
  const sk = RM ? (t) => (t >= 0.05 ? 1 : 0) : spring;
  const store = { get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }, set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} } };

  /* ------------------------------------------------------------ data */
  const BHAGWA = '#ED5A1E', CREAM = '#F3ECE0', HOT = '#FFC7A3';
  const S = [3.0, 8.4, 13.2, 18.0, 22.8], END = 27.6, STRETCH = 28.3, DUR = 31.2;
  const T0 = 3.5;                    // where the page rests at scroll 0: hero settled, buttons in
  const FLY = 1.25, LAND = 0.55;     // the ember leaves the rail at S-FLY, lands back on it at S+LAND
  const SPEED = clamp((parseFloat(root.dataset.y9Speed) || 140) / 100, 1, 2);
  // Age geometry. d/p are the intrinsic sizes of the desktop (16:9) and portrait (9:16) plates;
  // a/pa are the focal point (where the ember lands) as fractions of the plate; tx/ty and ptx/pty
  // are where on screen that point should sit; poster is the loop time the still was taken at.
  const AGES = [
    { name: 'SOURCE',    roman: 'I',   color: '#c9a961', bloom: '255,214,140' },
    { name: 'ERASED',    roman: 'II',  color: '#c08552', bloom: '255,190,120', poster: 2.0, d: [1920, 1080], p: [720, 1280], a: [0.635, 0.590], pa: [0.380, 0.430], tx: 0.68, ty: 0.50, ptx: 0.40, pty: 0.36 },
    { name: 'OPERATION', roman: 'III', color: '#c0c8d4', bloom: '220,230,255', poster: 1.5, d: [1920, 1080], p: [720, 1280], a: [0.625, 0.490], pa: [0.410, 0.470], tx: 0.62, ty: 0.42, ptx: 0.42, pty: 0.40 },
    { name: 'NOW',       roman: 'IV',  color: '#ED5A1E', bloom: '255,120,40',  poster: 2.0, d: [1920, 1080], p: [720, 1280], a: [0.581, 0.846], pa: [0.494, 0.872], tx: 0.60, ty: 0.72, ptx: 0.50, pty: 0.80 },
    { name: '2126',      roman: 'V',   color: '#5fa8d3', bloom: '140,200,255', poster: 1.5, d: [1920, 1080], p: [720, 1280], a: [0.700, 0.520], pa: [0.460, 0.260], tx: 0.66, ty: 0.42, ptx: 0.50, pty: 0.30 },
  ];
  const RAILNAMES = ['Source', 'Erased', 'Operation', 'Now', '2126'];
  const RAILDATES = (root.dataset.y9Dates || '').split('|');     // written in the section, so the Instrument switch reaches the rail too
  const FONTS = ['500 100px Oswald', 'italic 500 60px "Cormorant Garamond"', '400 12px "Share Tech Mono"', '500 60px "Rajdhani YL"'];
  // a loop starts fetching once the age before it has arrived
  const LOADAT = [0, T0 + 0.5, S[1] + 1.0, S[2] + 1.0, S[3] + 1.0];

  /* ------------------------------------------------------------ dom */
  const pin = $('[data-y9-pin]'), world = $('[data-y9-world]'), scrim = $('[data-y9-scrim]'), herobg = $('[data-y9-herobg]');
  const hero = $('[data-y9-hero]'), studio = $('[data-y9-studio]'), s1 = $('[data-y9-s1]'), s2 = $('[data-y9-s2]');
  const kick = $('[data-y9-kick]'), plain = $('[data-y9-plain]'), acts = $('[data-y9-acts]');
  const cue = $('[data-y9-cue]'), cueDot = cue ? cue.lastElementChild : null;
  const rail = $('[data-y9-rail]'), wm = $('[data-y9-wm]'), wmInner = $('[data-y9-wmin]'), wmHi = $('[data-y9-wmhi]'), wmEn = $('[data-y9-wmen]');
  const ruleTop = $('[data-y9-rtop]'), ruleBot = $('[data-y9-rbot]'), rA = $('[data-y9-ra]'), rB = $('[data-y9-rb]');
  const outsub = $('[data-y9-outsub]'), outhint = $('[data-y9-outhint]'), ptEl = $('[data-y9-pt]');
  if (!pin || !world || !ptEl) return;

  const mk = (cls, css) => { const e = document.createElement('i'); e.className = cls; if (css) e.style.cssText = css; return e; };
  const shots = AGES.map((a, i) => {
    const el = $('[data-y9-shot="' + i + '"]');
    const sh = { el, plate: $('[data-y9-plate]', el), poster: $('[data-y9-poster]', el), vid: $('[data-y9-vid]', el), dim: $('[data-y9-dim]', el), bloom: $('[data-y9-bloom]', el), key: null, pSet: false, vLoaded: false, vReady: false };
    sh.rings = [0, 1].map(() => { const r = mk('y9-ring'); r.style.borderColor = a.color; el.after(r); return r; });
    return sh;
  });
  const thread = mk('y9-thread'); world.append(thread);
  const trail = mk('y9-trail'); pin.append(trail);

  const txt = $$('[data-y9-age]').map((el) => ({
    el, i: +el.dataset.y9Age, top: el.hasAttribute('data-y9-top'),
    meta: $('.y9-meta', el), name: $('.y9-name', el), letters: $$('.y9-name span', el), line: $('.y9-line', el), enter: $('.y9-enter', el), chips: $$('.y9-tf', el),
  })).sort((a, b) => a.i - b.i);

  const R = { lines: [], ticks: [], labels: [], brk: null, fill: null };
  for (let j = 0; j < 2; j++) { const l = mk('y9-rl'); rail.append(l); R.lines.push(l); }
  R.fill = mk('y9-rf'); rail.append(R.fill);
  for (let i = 0; i < 5; i++) {
    const tk = mk('y9-tk'); rail.append(tk); R.ticks.push(tk);
    const lb = document.createElement('div'); lb.className = 'y9-tl'; lb.innerHTML = AGES[i].roman + '<span class="y9-nm"> ' + RAILNAMES[i] + '</span>' + (RAILDATES[i] ? '<span class="y9-td"></span>' : ''); const td = lb.querySelector('.y9-td'); if (td) td.textContent = RAILDATES[i]; rail.append(lb); R.labels.push(lb);
  }
  R.brk = document.createElement('div'); R.brk.className = 'y9-brk'; R.brk.textContent = '//'; rail.append(R.brk);

  /* ------------------------------------------------------------ layout */
  let W = 0, H = 0, SF = 68, TH = 160, PORT = false, LOW = false, PAD = 56, D = 1, railY = 0, x0 = 0, x1 = 0, TICK = [0, 0, 0, 0, 0], BRKX = 0, secTop = 0, pull = 0;
  const FIT = [], WM = {};
  let nameSize = [];
  let curT = -1, forced = true, active = true, raf = 0;

  const bump = (u) => (u > 0 ? Math.exp(-u * 2.3) * Math.sin(clamp(u / 0.22) * Math.PI / 2) : 0);
  const setVis = (el, on) => { const v = on ? 'visible' : 'hidden'; if (el.style.visibility !== v) el.style.visibility = v; };

  /* embers: a deterministic particle layer. One field per age, cross-faded by the ember's era.
     Every position is a pure function of ambient time g. */
  const EM = (function () {
    if (!VIDS) return { size() {}, draw() {}, degrade() {} };
    const cv = document.createElement('canvas'); cv.className = 'y9-embers'; cv.setAttribute('aria-hidden', 'true');
    pin.insertBefore(cv, scrim);
    const cx = cv.getContext('2d');
    if (!cx) return { size() {}, draw() {}, degrade() {} };
    const spr = AGES.map((a) => { const c = document.createElement('canvas'); c.width = c.height = 64; const g = c.getContext('2d'), gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
      gr.addColorStop(0, 'rgba(255,238,214,1)'); gr.addColorStop(0.16, 'rgba(' + a.bloom + ',.95)'); gr.addColorStop(0.42, 'rgba(' + a.bloom + ',.28)'); gr.addColorStop(1, 'rgba(' + a.bloom + ',0)');
      g.fillStyle = gr; g.fillRect(0, 0, 64, 64); return c; });
    // n particles, size range (px), vy/vx px/s, sway px, peak alpha, kind
    const SPEC = [
      { n: 52, s: [3, 9],  vy: -9,  vx: 3,  sw: 26, a: 0.55, kind: 'field' },   // I   gold motes rising through lamplight
      { n: 60, s: [3, 10], vy: 15,  vx: -7, sw: 34, a: 0.46, kind: 'field' },   // II  ash falling
      { n: 38, s: [2, 6],  vy: 1,   vx: 26, sw: 8,  a: 0.55, kind: 'field' },   // III cold specks crossing the frame
      { n: 70, s: [3, 11], vy: -70, vx: 0,  sw: 30, a: 0.95, kind: 'emit' },    // IV  embers leaving the flame
      { n: 76, s: [2, 6],  vy: -4,  vx: 0,  sw: 10, a: 0.7,  kind: 'twinkle' }, // V   points of light
    ];
    const P = SPEC.map((s, i) => { const r = rng(7700 + i * 131); return Array.from({ length: s.n }, () => { const z = 0.3 + 0.7 * r(); const big = r() < 0.09;
      return { x: r(), y: r(), z, ph: r() * 6.283, L: 2.6 + 2.8 * r(), big, spread: r() - 0.5 }; }); });
    let w = 0, h = 0, dpr = 1, lowK = 1, cut = 1;
    return {
      size() { w = W; h = H; dpr = Math.min(1.5, window.devicePixelRatio || 1); lowK = (PORT ? 0.62 : 1) * (LOW ? 0.6 : 1);
        cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); cv.style.width = w + 'px'; cv.style.height = h + 'px'; },
      degrade() { cut = 0.4; },
      draw(t, g, fidx) {
        cx.setTransform(dpr, 0, 0, dpr, 0, 0); cx.clearRect(0, 0, w, h);
        if (!w || !FIT[4]) return;
        const env = smooth(t, S[0] - 0.2, S[0] + 1.6) * (1 - smooth(t, END - 0.55, END + 0.45));
        cx.globalCompositeOperation = 'lighter';
        // lens streak: a thin horizontal flare when the ember lights each age
        for (let i = 0; i < 5; i++) {
          const u = t - (i === 0 ? 0.6 : S[i] - 0.3); if (u <= 0 || u > 2.2 || (i === 4 && t > END - 0.3)) continue;
          const a = Math.exp(-u * 2.5) * Math.sin(clamp(u / 0.12) * Math.PI / 2) * (i === 3 ? 0.85 : 0.6); if (a < 0.01) continue;
          const f = FIT[i], fx = f.ax - 14, fy = f.ay - 14, len = w * (PORT ? 0.8 : 0.95) * (1 - Math.exp(-u * 5.5)), th = PORT ? 7 : 11;
          cx.globalAlpha = Math.min(1, a); cx.drawImage(spr[i], fx - len / 2, fy - th / 2, len, th);
          cx.globalAlpha = Math.min(1, a * 0.55); cx.drawImage(spr[i], fx - len * 0.09, fy - len * 0.09, len * 0.18, len * 0.18);
        }
        if (env < 0.004) { cx.globalAlpha = 1; cx.globalCompositeOperation = 'source-over'; return; }
        for (let i = 0; i < 5; i++) {
          const wt = clamp(1 - Math.abs(fidx - i)) * env; if (wt < 0.01) continue;
          const s = SPEC[i], set = P[i], nUse = Math.ceil(s.n * lowK * cut), boost = 1 + (i > 0 ? 1.1 * bump(t - (S[i] - 0.3)) : 0);
          for (let j = 0; j < nUse; j++) {
            const p = set[j], zf = 0.5 + 0.9 * p.z;
            let x, y, al, sz = mix(s.s[0], s.s[1], p.z) * (p.big ? 3.4 : 1), ab = p.big ? 0.32 : 1;
            if (s.kind === 'emit') {
              const f = FIT[i], ox = f.ax - 14, oy = f.ay - 14, u = ((g / p.L + p.ph / 6.283) % 1 + 1) % 1;
              const rise = (0.28 + 0.5 * p.z) * h * u;
              x = ox + p.spread * (PORT ? 110 : 190) * (0.4 + u) + s.sw * Math.sin(u * 8 + p.ph) * u; y = oy - rise;
              al = Math.pow(Math.sin(Math.PI * u), 1.15); sz *= 1 - 0.55 * u;
            } else if (s.kind === 'field') {
              x = (((p.x * w + s.vx * zf * g + s.sw * Math.sin(g * 0.37 + p.ph)) % w) + w) % w;
              y = (((p.y * h + s.vy * zf * g) % h) + h) % h;
              al = Math.min(1, y / 70, (h - y) / 70);
            } else {
              x = p.x * w + s.sw * Math.sin(g * 0.23 + p.ph); y = (((p.y * h + s.vy * zf * g) % h) + h) % h;
              al = (0.45 + 0.55 * Math.sin(g * (0.7 + 0.9 * p.z) + p.ph * 3.1)) * Math.min(1, y / 70, (h - y) / 70);
            }
            al *= s.a * ab * (0.35 + 0.65 * p.z) * wt * boost; if (al < 0.01) continue;
            cx.globalAlpha = Math.min(1, al); cx.drawImage(spr[i], x - sz, y - sz, sz * 2, sz * 2);
          }
        }
        cx.globalAlpha = 1; cx.globalCompositeOperation = 'source-over';
      },
    };
  })();

  function fit(i) {
    const a = AGES[i];
    if (i === 0) {   // the Sri Yantra: a square, centred on the bindu
      const room = W >= 1200 ? W - PAD - (PAD + SF * 10.4 + 30) : W * 0.58;   // leave the studio line its column (its width is 10.4 headline sizes, as in the CSS)
      const size = PORT ? Math.min(W * 0.94, H * 0.5) : Math.min(H * (W >= 1200 ? 0.82 : 0.72), room);
      const ax = PORT ? W * 0.5 : W - PAD - size * 0.5, ay = PORT ? H * 0.31 : H * 0.46;
      return { dw: size, dh: size, ox: ax - size / 2, oy: ay - size / 2, ax, ay, key: 'y' };
    }
    const iw = PORT ? a.p[0] : a.d[0], ih = PORT ? a.p[1] : a.d[1], an = PORT ? a.pa : a.a;
    const s = Math.max(W / iw, H / ih) * 1.035, dw = iw * s, dh = ih * s;
    const tx = PORT ? a.ptx : a.tx, ty = PORT ? a.pty : a.ty, slx = dw - W, sly = dh - H;
    let ox = 0, oy = 0;
    if (slx > 0.5) ox = -clamp((an[0] * dw - tx * W) / slx) * slx;
    if (sly > 0.5) oy = -clamp((an[1] * dh - ty * H) / sly) * sly;
    return { dw, dh, ox, oy, ax: ox + an[0] * dw, ay: oy + an[1] * dh, key: PORT ? 'p' : 'd' };
  }

  // The stage starts under the site bar. Pull it up by everything that sits above it.
  function measurePull() {
    const cur = -(parseFloat(getComputedStyle(root).marginTop) || 0);
    const top = root.getBoundingClientRect().top + window.scrollY;
    pull = cur;
    if (Math.abs(top) > 0.5) { pull = Math.max(0, cur + top); root.style.setProperty('--y9-pull', pull + 'px'); }
    secTop = root.getBoundingClientRect().top + window.scrollY;
  }

  function setPoster(sh, i) {
    if (!sh.poster || !sh.pSet) return;
    const src = sh.key === 'p' ? sh.poster.dataset.pp : sh.poster.dataset.dp;
    if (sh.poster.getAttribute('src') !== src) sh.poster.src = src;
  }
  function dropVideo(sh) {
    if (!sh.vid || !sh.vLoaded) return;
    try { sh.vid.pause(); } catch (e) {} sh.vid.removeAttribute('src'); sh.vid.load(); sh.vid.classList.remove('is-on'); sh.vLoaded = sh.vReady = false;
  }

  function layout() {
    W = document.documentElement.clientWidth; H = window.innerHeight;
    PORT = W < 768 || H > W * 1.15; LOW = H < 500; PAD = W < 768 ? 20 : W < 1200 ? 36 : 56;
    measurePull();
    root.style.setProperty('--y9-hdr', (pull + (LOW ? 0 : W < 768 ? 4 : 20)) + 'px');
    SF = LOW ? clamp(H * 0.1, 22, 34) : PORT ? clamp(W / 9.6, 36, 46) : clamp(W * 0.046, 46, 100);
    TH = PORT ? (W < 768 ? 72 : 88) : W >= 1440 ? 160 : W >= 1200 ? 140 : 104;      // the works row, as in the CSS
    pin.classList.toggle('is-port', PORT); pin.classList.toggle('is-tab', !PORT && W < 1200); pin.classList.toggle('is-low', LOW);
    EM.size();
    D = Math.round(H * 6.9);
    root.style.height = (H + D) + 'px'; pin.style.height = H + 'px';
    for (let i = 0; i < 5; i++) {
      const f = fit(i); FIT[i] = f; const sh = shots[i];
      if (sh.key !== f.key) { const first = sh.key === null; sh.key = f.key; if (!first) { dropVideo(sh); setPoster(sh, i); } }
      Object.assign(sh.plate.style, { width: f.dw + 'px', height: f.dh + 'px', left: f.ox + 'px', top: f.oy + 'px', transformOrigin: (f.ax - f.ox) + 'px ' + (f.ay - f.oy) + 'px' });
    }
    // rail
    railY = H - (LOW ? 30 : PORT ? 44 : 76); x0 = PAD + 4; x1 = W - PAD - 4;
    const f3 = clamp((FIT[3].ax - x0) / (x1 - x0), 0.5, 0.8), x3 = mix(x0, x1, f3);
    TICK = [x0, mix(x0, x3, 0.34), mix(x0, x3, 0.66), x3, x1]; BRKX = mix(TICK[0], TICK[1], 0.5);
    // names, fitted to the room the copy has
    const c2 = document.createElement('canvas').getContext('2d'); c2.font = '500 100px Oswald, Arial Narrow, sans-serif';
    nameSize = AGES.map((a, i) => { const w100 = c2.measureText(a.name).width - 1.2 * a.name.length; const avail = PORT ? W - 2 * PAD : i === 0 ? FIT[0].ox - PAD - 20 : W - 2 * PAD - (TH * 3.444 + 28) - 64; return Math.floor(Math.min(H * (LOW ? 0.2 : PORT ? 0.24 : 0.3), avail / w100 * 100)); });
    txt.forEach((tx) => { tx.name.style.fontSize = nameSize[tx.i] + 'px'; tx.el.classList.toggle('is-top', PORT && tx.top); });
    root.style.setProperty('--y9-sf', SF + 'px');   // the inline script in the section sets the same value before first paint
    R.ticks.forEach((tk, i) => { tk.style.transform = 'translate(' + TICK[i] + 'px, ' + (railY - 5) + 'px)'; });
    R.labels.forEach((lb, i) => { lb.style.textAlign = i === 0 ? 'left' : i === R.labels.length - 1 ? 'right' : 'center'; const td = lb.querySelector('.y9-td'); if (td) td.style.display = PORT || LOW ? 'none' : ''; lb.style.transform = 'translate(' + TICK[i] + 'px, ' + (railY + 14) + 'px) translateX(' + (i === 0 ? '0px' : i === R.labels.length - 1 ? 'calc(-100% + .18em)' : '-50%') + ')'; $('.y9-nm', lb).style.display = PORT ? 'none' : 'inline'; });
    R.brk.style.transform = 'translate(' + (BRKX - 8) + 'px, ' + (railY - 7) + 'px)';
    if (cue) { cue.style.transform = 'translate(' + (W / 2) + 'px, ' + (railY - 66) + 'px)'; cue.style.display = LOW ? 'none' : ''; }
    R.lines[0].style.transform = 'translate(' + TICK[0] + 'px, ' + railY + 'px)'; R.lines[0].style.width = (BRKX - 16 - TICK[0]) + 'px';
    R.lines[1].style.transform = 'translate(' + (BRKX + 16) + 'px, ' + railY + 'px)'; R.lines[1].style.width = (x1 - BRKX - 16) + 'px';
    R.fill.style.transform = 'translate(' + TICK[0] + 'px, ' + (railY - 0.5) + 'px)';
    measureWordmark();
  }

  function measureWordmark() {
    let Sx = W >= 1200 ? 1.3 : W >= 768 ? 1.0 : Math.min(0.9, W * 0.84 / 585); if (LOW) Sx = Math.min(Sx, H * 0.5 / 230); WM.S = Sx;
    wm.style.gap = (18 * Sx) + 'px'; wm.style.padding = (14 * Sx) + 'px 0';
    ruleTop.style.height = ruleBot.style.height = (2 * Sx) + 'px';
    wmInner.style.gap = (6 * Sx) + 'px'; wmInner.style.padding = (4 * Sx) + 'px 0';
    wmHi.style.fontSize = (38 * Sx) + 'px'; wmEn.style.fontSize = (108 * Sx) + 'px';
    wm.style.left = '0px'; wm.style.top = '0px'; wm.style.transform = 'none'; wm.style.clipPath = 'none';
    let w = wm.getBoundingClientRect(); const cy = H / 2 - (PORT ? 30 : 18);
    wm.style.left = (W / 2 - w.width / 2) + 'px'; wm.style.top = (cy - w.height / 2) + 'px';
    w = wm.getBoundingClientRect();
    const pr = pin.getBoundingClientRect();
    const rt = ruleTop.getBoundingClientRect(), rb = ruleBot.getBoundingClientRect();
    // measured in pin space, so the numbers stay right wherever the pin sits in the viewport
    const wp = { top: w.top - pr.top, bottom: w.bottom - pr.top, left: w.left - pr.left, right: w.right - pr.left, width: w.width, height: w.height };
    Object.assign(WM, { w: wp, ruleH: rt.height, L: rt.left - pr.left, R: rt.right - pr.left, topC: rt.top - pr.top + rt.height / 2, botC: rb.top - pr.top + rb.height / 2, cx: W / 2, cy });
    outsub.style.top = (wp.bottom + (PORT ? 34 : 44)) + 'px'; outsub.style.fontSize = (PORT ? 20 : 28) + 'px'; outsub.style.padding = '0 ' + PAD + 'px';
    outhint.style.top = (railY - 18) + 'px';
  }

  /* ------------------------------------------------------------ loops: fetched late, played only while their age is on screen */
  function ensureVideo(i) {
    const sh = shots[i]; if (!VIDS || !sh.vid || sh.vLoaded) return;
    sh.vLoaded = true; const v = sh.vid;
    v.addEventListener('playing', () => v.classList.add('is-on'), { once: true });
    v.addEventListener('loadedmetadata', () => { try { v.currentTime = AGES[i].poster; } catch (e) {} sh.vReady = true; }, { once: true });
    v.preload = 'auto'; v.src = sh.key === 'p' ? v.dataset.pv : v.dataset.dv;
  }
  function setPlay(i, want) {
    const sh = shots[i]; if (!sh.vLoaded || !sh.vReady) return; const v = sh.vid;
    if (want && v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
    else if (!want && !v.paused) v.pause();
  }
  const pauseAll = () => shots.forEach((sh, i) => setPlay(i, false));

  /* ------------------------------------------------------------ paint: a pure function of t */
  function reveal(el, tin, tout, dy) {
    const a = tin === null ? 1 : sp(tin, 'base'), b = tout === null ? 0 : sp(tout, 'base');
    const on = a > 0.002 && b < 0.998;
    setVis(el, on);
    if (on) { el.style.opacity = clamp(a * 1.6) * (1 - clamp(b * 1.6)); el.style.transform = 'translateY(' + ((1 - a) * dy - b * dy * 0.6) + 'px)'; }
    return on;
  }
  function ptXY(t) {
    const A0 = FIT[0], trav = sp(t - 1.25, 'heavy');
    let x = mix(A0.ax, TICK[0], trav), y = mix(A0.ay, railY, trav), fly = 0;
    for (let i = 1; i < 5; i++) {
      const F = FIT[i], u = RM ? 0 : sp(t - (S[i] - FLY), 'base'), v = sp(t - (S[i] + LAND), 'heavy');
      x = mix(x, F.ax, u); y = mix(y, F.ay, u) - Math.sin(Math.PI * clamp(u)) * Math.min(96, H * 0.12);
      x = mix(x, TICK[i], v); y = mix(y, railY, v);
      fly = Math.max(fly, Math.sin(Math.PI * clamp(u)) * (1 - clamp(v)));
    }
    const o = sp(t - 27.3, 'heavy');
    return [mix(x, WM.cx, o), mix(y, WM.cy, o), fly];
  }

  // t drives the story; g is ambient time (flame flicker, slow breathing, embers). g defaults to t.
  function paint(t, g) {
    if (g === undefined || RM) g = t;
    const st = {};
    // ---- the ember: position, size, colour
    let fidx = 0; for (let i = 1; i < 5; i++) fidx += sp(t - (S[i] - 0.15), 'heavy');
    const [px, py, fly] = ptXY(t); let rx = TICK[0];
    for (let i = 1; i < 5; i++) rx += (TICK[i] - TICK[i - 1]) * sp(t - (S[i] + LAND), 'heavy');
    const size = track(t, [[0, 0], [0.35, 22], [1.4, 10]], 170, 26);
    const rad = (size / 2) * (1 - sk(t - 1.5, 170, 26));
    const color = t < 1.3 ? mixColor(HOT, BHAGWA, smooth(t, 0.35, 1.3)) : BHAGWA;
    let glow = clamp(size / 22) * (0.35 + 0.65 * (1 - smooth(t, 0.9, 1.8))) * (1 + 0.8 * (1 - smooth(t, 0.35, 0.75)));
    for (let i = 1; i < 5; i++) glow += 0.75 * bump(t - (S[i] - 0.3));
    glow += 0.5 * fly;
    const show = t >= 0.35 && t < STRETCH;
    st.pt = show ? { x: px, y: py, s: size, rad, color, glow, a: 1 } : { x: px, y: py, s: 0, rad: 0, color, glow: 0, a: 0 };

    { const [qx, qy] = ptXY(t - 0.16), dx = px - qx, dy = py - qy, len = Math.hypot(dx, dy);
      const vis = show && !RM && len > 8 ? clamp((len - 8) / 50) * 0.85 : 0;
      trail.style.opacity = vis.toFixed(3);
      if (vis > 0) { trail.style.width = len + 'px'; trail.style.transform = 'translate(' + qx + 'px, ' + qy + 'px) rotate(' + Math.atan2(dy, dx) + 'rad)'; } }
    // era colour follows the ember
    const fi = Math.min(3.999, fidx), i0 = Math.floor(fi), fr = fi - i0;
    const eraC = i0 >= 4 ? AGES[4].color : mixColor(AGES[i0].color, AGES[i0 + 1].color, smooth(fr, 0.15, 0.85));
    pin.style.setProperty('--y9-era', eraC);

    // ---- shots: each age is lit from its own focal point
    const playOK = VIDS && active && !document.hidden;
    for (let i = 0; i < 5; i++) {
      const sh = shots[i], s = S[i], f = FIT[i];
      const t0 = i === 0 ? 0.55 : s - 0.3;
      const op = i === 4 ? 1 - smooth(t, END - 0.55, END + 0.45) : 1;
      const covered = i < 4 && t > S[i + 1] + 1.5;
      const on = t > t0 - 0.25 && op > 0.002 && !covered;
      if (i > 0 && t > LOADAT[i] && op > 0.002 && !covered) ensureVideo(i);
      setVis(sh.el, on);
      if (!on) { sh.rings.forEach((r) => { r.style.opacity = 0; }); if (i > 0) setPlay(i, false); continue; }
      if (i > 0) setPlay(i, playOK);
      sh.el.style.opacity = op.toFixed(3);
      const bri = i === 0 ? mix(0.42, 1, smooth(t, 0.6, 2.4)) : mix(0.3, 1, smooth(t, s - 0.1, s + 1.6));
      sh.dim.style.opacity = (1 - bri).toFixed(3);
      const k = RM ? 1 : (1 + 0.16 * (1 - sp(t - t0, 'drift'))) * (1 + 0.007 * Math.sin(g * 0.8 + i));
      sh.plate.style.transform = 'scale(' + k.toFixed(4) + ')';
      { const fl = 0.86 + 0.14 * rng(4000 + i * 977 + Math.floor(g * 14))(); const base = i === 0 ? smooth(t, 0.5, 1.6) : smooth(t, s - 0.2, s + 1.0);
        const sz = i === 0 ? f.dw * 1.1 : (i === 3 ? 420 : 300) * (PORT ? 0.8 : 1);
        sh.bloom.style.width = sh.bloom.style.height = sz + 'px'; sh.bloom.style.transform = 'translate(' + (f.ax - sz / 2) + 'px, ' + (f.ay - sz / 2) + 'px)'; sh.bloom.style.opacity = (base * fl * (i === 3 ? 0.95 : 0.6)).toFixed(3); }
      const far = Math.max(Math.hypot(f.ax, f.ay), Math.hypot(W - f.ax, f.ay), Math.hypot(f.ax, H - f.ay), Math.hypot(W - f.ax, H - f.ay)) * 1.14;
      const Rr = far * (i === 0 ? sp(t - t0, 'heavy') : sk(t - t0, 22, 9.4));
      if (Rr < far * 0.985) { const fe = 60 + Rr * 0.26; const m = 'radial-gradient(circle at ' + f.ax + 'px ' + f.ay + 'px, #000 ' + Math.max(0, Rr - fe) + 'px, transparent ' + Rr + 'px)'; sh.el.style.webkitMaskImage = m; sh.el.style.maskImage = m; }
      else { sh.el.style.webkitMaskImage = 'none'; sh.el.style.maskImage = 'none'; }
      // rings at the focal point: one pair per arrival
      const ts = i === 0 ? 0.6 : s - 0.3;
      sh.rings.forEach((r, j) => {
        const u = t - (ts + j * 0.3), p = clamp(u / 1.6);
        if (RM || u <= 0 || p >= 1) { r.style.opacity = 0; return; }
        const e = 1 - Math.pow(1 - p, 3);
        r.style.opacity = (0.5 * Math.pow(1 - p, 1.4)).toFixed(3);
        r.style.transform = 'translate(' + f.ax + 'px, ' + f.ay + 'px) scale(' + mix(0.04, 1, e).toFixed(3) + ')';
      });
    }
    // thread: from the Now age's flame to the ember, drawn at arrival, then fades
    {
      let ti = -1; for (let i = 3; i < 4; i++) if (t >= S[i] + LAND && t < S[i + 1] - 0.2) ti = i;
      if (ti >= 0 && !RM) {
        const s0 = S[ti] + LAND, e1 = S[ti + 1] - 0.2;
        const f = FIT[ti], dx = px - f.ax, dy = py - f.ay, len = Math.hypot(dx, dy), ang = Math.atan2(dy, dx);
        const draw = sp(t - s0, 'base'), fade = 1 - smooth(t, e1 - 0.7, e1);
        thread.style.width = (len * draw) + 'px'; thread.style.background = AGES[ti].color; thread.style.opacity = (0.38 * fade).toFixed(3);
        thread.style.transform = 'translate(' + f.ax + 'px, ' + f.ay + 'px) rotate(' + ang + 'rad)';
      } else thread.style.opacity = 0;
    }
    scrim.style.opacity = (smooth(t, 0.8, 2.4) * (1 - smooth(t, END - 0.3, END + 0.5))).toFixed(3);
    EM.draw(t, g, fidx);

    // ---- hero: the studio line and two doors. At rest from the first paint (it is the LCP),
    //      so only the exit is animated: it goes when the first age begins.
    {
      const out = 3.7, heroOn = t < out + 1.4;
      setVis(hero, heroOn); herobg.style.opacity = (1 - smooth(t, out, out + 1.0)).toFixed(3);
      if (heroOn) {
        const mask = (el, lag) => { const b = sp(t - out - lag, 'base'); const inner = el.firstElementChild; inner.style.transform = 'translateY(' + (-b * 108) + '%)'; inner.style.opacity = (1 - clamp(b * 1.4)).toFixed(3); };
        mask(s1, 0); mask(s2, 0.06);
        const fade = (el, dy) => { const b = sp(t - out, 'base'); el.style.opacity = (1 - clamp(b * 1.5)).toFixed(3); el.style.transform = 'translateY(' + (-b * dy) + 'px)'; };
        fade(kick, 14); fade(plain, 18); fade(acts, 22);
      }
      // a first-time visitor is not told the stage scrolls: one small cue at rest, gone as soon as the first age begins
      if (cue) {
        cue.style.opacity = (smooth(t, 1.8, 2.6) * (1 - smooth(t, out - 0.4, out + 0.4))).toFixed(3);
        if (cueDot) cueDot.style.transform = 'translateY(' + (RM ? 0 : ((g * 0.9) % 1) * 22).toFixed(1) + 'px)';
      }
    }
    // ---- ages
    for (let i = 0; i < txt.length; i++) {
      const T = txt[i], n = T.i, base = S[n] + (n === 0 ? 1.55 : 0), ex = (n < 4 ? S[n + 1] : END) - 0.95;
      const on = t > base && t < ex + 1.5;
      const disp = on ? 'block' : 'none'; if (T.el.style.display !== disp) T.el.style.display = disp;
      if (!on) continue;
      reveal(T.meta, t - (base + 0.15), t - ex, 12);
      T.letters.forEach((l, j) => {
        const a = sp(t - (base + 0.3 + 0.05 * j), 'heavy'), b = sp(t - (ex + 0.03 * j), 'base');
        l.style.transform = 'translateY(' + ((1 - a) * 112 - b * 112).toFixed(2) + '%)'; l.style.opacity = (1 - clamp(b * 1.6)).toFixed(3);
      });
      reveal(T.line, t - (base + 0.85), t - (ex + 0.06), 20);
      if (T.enter) reveal(T.enter, t - (base + 1.0), t - (ex + 0.07), 18);
      T.chips.forEach((c, j) => reveal(c, t - (base + 1.15 + 0.11 * j), t - (ex + 0.08 + 0.03 * j), 26));
    }
    // ---- rail
    {
      const railP = sp(t - 1.75, 'base'), railA = smooth(t, 1.7, 2.3) * (1 - smooth(t, 27.2, 27.9));
      rail.style.opacity = railA.toFixed(3);
      const rev = mix(TICK[0], x1, railP), segs = [[TICK[0], BRKX - 16], [BRKX + 16, x1]];
      R.lines.forEach((l, j) => { const a = segs[j][0], b = segs[j][1]; l.style.transform = 'translate(' + a + 'px, ' + railY + 'px) scaleX(' + clamp((rev - a) / (b - a)).toFixed(4) + ')'; });
      R.fill.style.width = Math.max(0, Math.min(clamp(rx - TICK[0], 0, 1e5), x1 - TICK[0])) + 'px';
      R.ticks.forEach((tk, i) => { tk.style.opacity = (TICK[i] <= rev + 1 ? 1 : 0); });
      R.labels.forEach((lb, i) => { const o = clamp(1 - Math.abs(fidx - i)); lb.style.opacity = (0.66 + 0.34 * o).toFixed(3); lb.style.color = mixColor(CREAM, AGES[i].color, o * 0.85); lb.style.display = TICK[i] <= rev + 1 ? '' : 'none'; });
      R.brk.style.opacity = rev > BRKX ? 0.6 : 0;
    }
    // ---- ident: the ember becomes the rules; the wordmark is revealed between them
    {
      const on = t >= STRETCH;
      wm.style.visibility = on ? 'visible' : 'hidden'; rA.style.display = rB.style.display = on ? 'block' : 'none';
      if (on) {
        const w = WM.w, Pw = 10, cx = WM.cx, cy = WM.cy, ts = STRETCH, split = ts + 0.5;
        const L = mix(cx - Pw / 2, WM.L, sk(t - ts - 0.05, 320, 30)), Rr = mix(cx + Pw / 2, WM.R, sk(t - ts - 0.1, 140, 22));
        const h = mix(Pw, WM.ruleH, sk(t - ts, 520, 44)), yc = cy, sp2 = sp(t - split, 'base');
        const ya = mix(yc, WM.topC, sp2), yb = mix(yc, WM.botC, sp2);
        [[rA, ya], [rB, yb]].forEach((pair) => { const el = pair[0], y = pair[1]; el.style.width = (Rr - L) + 'px'; el.style.height = h + 'px'; el.style.transform = 'translate(' + L + 'px, ' + (y - h / 2) + 'px)'; });
        const clipTop = Math.max(0, ya + h / 2 - w.top), clipBot = Math.max(0, w.bottom - (yb - h / 2));
        wm.style.clipPath = 'inset(' + clipTop + 'px 0 ' + clipBot + 'px 0)';
        wmEn.style.transform = 'translateY(' + (12 * WM.S * (1 - sp(t - split - 0.02, 'base'))) + 'px)';
        wmHi.style.transform = 'translateY(' + (10 * WM.S * (1 - sp(t - split - 0.11, 'base'))) + 'px)';
        const a2 = sp(t - 29.4, 'base'); outsub.style.opacity = a2.toFixed(3); outsub.style.transform = 'translateY(' + ((1 - a2) * 16) + 'px)';
        const a3 = sp(t - 30.2, 'base'); outhint.style.opacity = (0.7 * a3).toFixed(3); setVis(outhint, a3 > 0.05);
      } else { outsub.style.opacity = 0; outhint.style.opacity = 0; setVis(outhint, false); }
    }
    return st;
  }

  function drawPoint(p) {
    if (!p || p.a <= 0.001 || p.s <= 0.01) { ptEl.style.opacity = 0; return; }
    const g = p.glow;
    ptEl.style.opacity = p.a;
    ptEl.style.width = ptEl.style.height = p.s + 'px';
    ptEl.style.transform = 'translate(' + (p.x - p.s / 2).toFixed(2) + 'px, ' + (p.y - p.s / 2).toFixed(2) + 'px)';
    ptEl.style.borderRadius = p.rad + 'px'; ptEl.style.background = p.color;
    ptEl.style.boxShadow = g > 0.001 ? '0 0 ' + (44 * g).toFixed(1) + 'px ' + (9 * g).toFixed(1) + 'px rgba(237,90,30,' + (0.45 * g).toFixed(3) + '), 0 0 ' + (12 * g).toFixed(1) + 'px ' + (2 * g).toFixed(1) + 'px rgba(255,199,163,' + (0.55 * g).toFixed(3) + ')' : 'none';
  }

  /* ------------------------------------------------------------ live: the scrollbar is the playhead */
  const introSeen = store.get('yl9intro') === '1' && !Q.has('intro');
  let introStart = null, introDone = RM || introSeen || Q.has('skip') || window.scrollY > 40;
  const par = { x: 0, y: 0, tx: 0, ty: 0 };
  const finishIntro = () => { if (!introDone) { introDone = true; store.set('yl9intro', '1'); forced = true; } };

  function currentT(now) {
    const y = window.scrollY - secTop, tScroll = T0 + clamp(y / D) * (DUR - T0);
    if (!introDone && y > 40) finishIntro();
    if (introDone) return tScroll;
    const ic = introStart === null ? 0 : Math.min(T0, (now - introStart) / 1000 * SPEED);
    if (ic >= T0) { finishIntro(); return tScroll; }
    return ic;
  }

  let last = 0, ema = 16, nFrames = 0, slow = false;
  function frame(now) {
    raf = 0;
    if (!active || document.hidden) return;
    const dtms = last ? now - last : 16; last = now;
    if (!slow && ++nFrames > 90) { ema = ema * 0.94 + Math.min(dtms, 200) * 0.06; if (ema > 40) { slow = true; EM.degrade(); } }
    const t = currentT(now), y = window.scrollY - secTop, inStage = y < D + 2;
    if (Math.abs(t - curT) > 1e-5 || forced || (inStage && !RM)) {
      curT = t; forced = false;
      const st = paint(t, now / 1000); drawPoint(st.pt);
    }
    if (!RM && !PORT && inStage) { par.x += (par.tx - par.x) * 0.1; par.y += (par.ty - par.y) * 0.1; world.style.transform = 'translate3d(' + (par.x * 9).toFixed(2) + 'px, ' + (par.y * 6).toFixed(2) + 'px, 0)'; }
    raf = requestAnimationFrame(frame);
  }
  const kickLoop = () => { if (!raf && active && !document.hidden) raf = requestAnimationFrame(frame); };

  // the stage only runs while it is on screen
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((es) => { const on = es[es.length - 1].isIntersecting; if (on === active) return; active = on; if (on) { forced = true; kickLoop(); } else pauseAll(); }, { rootMargin: '120px 0px' }).observe(root);
  }
  document.addEventListener('visibilitychange', () => { if (document.hidden) pauseAll(); else { forced = true; kickLoop(); } });
  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((ev) => window.addEventListener(ev, finishIntro, { passive: true }));
  window.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') { par.tx = (e.clientX / W - 0.5) * -1; par.ty = (e.clientY / H - 0.5) * -1; } }, { passive: true });

  // doors inside the stage jump straight to their section (no smooth scroll through five ages)
  root.addEventListener('click', (e) => {
    const a = e.target.closest('[data-y9-jump]'); if (!a) return;
    const id = (a.getAttribute('href') || '').replace(/^#/, ''), el = id && document.getElementById(id); if (!el) return;
    e.preventDefault(); finishIntro();
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: 'instant' });   // the site's smooth-scroll would play the whole stage
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: true }); } catch (err) {}
    try { history.replaceState(null, '', '#' + id); } catch (err) {}
  });

  /* ------------------------------------------------------------ go */
  layout();
  introStart = performance.now(); kickLoop();          // nothing waits for fonts: the hero is already on screen
  const relayout = () => { layout(); forced = true; kickLoop(); };
  let rw = W, rh = H;
  window.addEventListener('resize', () => { const nw = document.documentElement.clientWidth, nh = window.innerHeight; if (nw === rw && Math.abs(nh - rh) < 140) return; rw = nw; rh = nh; layout(); forced = true; kickLoop(); });
  window.addEventListener('load', () => {
    relayout();
    // the wordmark fonts (Rajdhani, Devanagari) are only needed at the end of the story: fetch them once the page has settled, then re-measure
    Promise.all(FONTS.map((f) => document.fonts.load(f, 'AaYL 2126 योद्धा').catch(() => {}))).then(() => document.fonts.ready).then(relayout);
    // posters for ages II to V, then the little cover frames, once the page itself has settled
    const later = () => { shots.forEach((sh, i) => { if (i > 0) { sh.pSet = true; setPoster(sh, i); } }); if (!SAVE) $$('.y9-tf img').forEach((im) => { im.loading = 'eager'; }); };
    (window.requestIdleCallback || ((f) => setTimeout(f, 400)))(later, { timeout: 1500 });
  });
  kickLoop();

  if (Q.has('y9debug')) window.y9 = { seek: (t, g) => { const st = paint(t, g); drawPoint(st.pt); return true; }, layout, FIT, WM, S, DUR, T0, get D() { return D; }, get pull() { return pull; }, shots };
})();
